import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@ontruck/orc';
import { useTranslation } from 'react-i18next';
import { useOffers } from 'hooks/useOffers/useOffers';
import { putRejectOffer } from 'utils/network/http';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'utils/constants/routes';
import { sendEventToSegment } from 'utils/functions/Tracking';
import TRACKING_EVENTS from 'utils/constants/trackingEvents';
import ButtonLoading from 'components/ButtonLoading';
import { useAuth } from 'hooks/useAuth/useAuth';

const OfferActionsList = ({
  className,
  offerId,
  requested,
  alreadyBid,
  biddableOffer
}) => {
  const { isAutoLogin, isDriverActive } = useAuth();
  const { t } = useTranslation('Offers');
  const {
    silentFetchOffers,
    startPollingOffers,
    cancelPollingOffers
  } = useOffers();

  const [viewLoading, setViewLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [error, setError] = useState(false);
  let history = useHistory();
  const loading = viewLoading && rejectLoading;

  const handleViewOfferDetail = useCallback(
    async e => {
      setViewLoading(true);
      e.stopPropagation();
      history.push(`${ROUTES.OFFER_DETAIL.DEFAULT}/${offerId}`);
      setViewLoading(false);
    },
    [offerId, history, setViewLoading]
  );

  const handleRejectOffer = useCallback(
    async e => {
      e.stopPropagation();
      try {
        setRejectLoading(true);
        setError(false);
        cancelPollingOffers();
        sendEventToSegment(TRACKING_EVENTS.OFFER_REJECT_FROM_CARD, {
          metaOfferId: offerId,
          isAutoLogin
        });
        await putRejectOffer(offerId);
        await silentFetchOffers();
      } catch (err) {
        console.error(err);
        setError(true);
      } finally {
        setRejectLoading(false);
        startPollingOffers();
      }
    },
    [
      silentFetchOffers,
      offerId,
      cancelPollingOffers,
      startPollingOffers,
      isAutoLogin
    ]
  );

  const renderBiddingActions = () => {
    return (
      <>
        {!loading && !error && alreadyBid && (
          <div
            className="offer-actions__requested-container"
            data-testid="bid-container"
          >
            <div className="offer-actions__requested-icon">
              <Icon glyph="coins" size="1.6rem" />
            </div>
            <div className="offer-actions__requested-body">
              <div className="offer-actions__requested-title">
                <span>{t('offerBid')}</span>
              </div>
            </div>
          </div>
        )}
        {!error && !alreadyBid && (
          <>
            <ButtonLoading
              testId="view-offer-button"
              loading={viewLoading}
              disabled={rejectLoading || !isDriverActive}
              handleClick={handleViewOfferDetail}
              extraClass="offer-actions__primary"
              text={t('view')}
            />
            <ButtonLoading
              testId="reject-offer-button"
              loading={rejectLoading}
              disabled={rejectLoading || !isDriverActive}
              handleClick={handleRejectOffer}
              extraClass="offer-actions__secondary"
              iconPosition="left"
              title={t('reject')}
              icon="trash"
              ghost
            />
          </>
        )}
      </>
    );
  };

  const renderNonBiddingActions = () => {
    return (
      <>
        {!loading && !error && requested && (
          <div
            className="offer-actions__requested-container"
            data-testid="requested-container"
          >
            <div className="offer-actions__requested-icon">
              <Icon glyph="time" size="1.6rem" />
            </div>
            <div className="offer-actions__requested-body">
              <div className="offer-actions__requested-title">
                <span>{t('offerRequested')}</span>
              </div>
            </div>
          </div>
        )}
        {!error && !requested && (
          <>
            <ButtonLoading
              testId="view-offer-button"
              loading={viewLoading}
              disabled={rejectLoading || !isDriverActive}
              handleClick={handleViewOfferDetail}
              extraClass="offer-actions__primary"
              text={t('view')}
            />
            <ButtonLoading
              testId="reject-offer-button"
              loading={rejectLoading}
              disabled={rejectLoading || !isDriverActive}
              handleClick={handleRejectOffer}
              extraClass="offer-actions__secondary"
              iconPosition="left"
              title={t('reject')}
              icon="trash"
              ghost
            />
          </>
        )}
      </>
    );
  };

  const showBiddingActions = biddableOffer
    ? biddableOffer && !requested
    : false;

  return (
    <div className={className} data-testid="offer-actions">
      <div className="offer-actions">
        {showBiddingActions
          ? renderBiddingActions()
          : renderNonBiddingActions()}
        {error && (
          <div className="offer-actions__error">
            <Icon glyph="close" size="2rem" />
          </div>
        )}
      </div>
    </div>
  );
};

OfferActionsList.defaultProps = {
  className: '',
  requested: false,
  alreadyBid: false,
  biddableOffer: false
};

OfferActionsList.propTypes = {
  className: PropTypes.string,
  offerId: PropTypes.number.isRequired,
  requested: PropTypes.bool,
  alreadyBid: PropTypes.bool,
  biddableOffer: PropTypes.bool
};

export default OfferActionsList;
